import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useBrand } from '@/brand/context'

/**
 * "Continue with Google" — an `<a>`, not a button.
 *
 * `GET /api/v1/auth/google/start` answers with a redirect to Google's consent
 * screen, so this has to be a full navigation. Fetching it instead would hand
 * the consent page to `fetch` as an opaque response and set the OAuth state
 * cookie on a request the browser then throws away.
 *
 * The URL is the server's, not this component's: the client id and the exact
 * `redirect_uri` are the server's to know, and a browser that composed the URL
 * itself would be a browser that could be persuaded to compose a different one.
 * That is `/google/start`'s own argument, and this is the other end of it.
 *
 * **`acceptedTerms` is what the signup page sends and the login page does not**
 * (S-B12). A Google callback that would *create* an account needs a
 * demonstrable consent under DPDP, and a redirect from Google carries no body of
 * ours to carry one — so the agreement is observed here, at `/google/start`,
 * and parked in the same `HttpOnly` cookie the OAuth state travels in. An
 * existing account signs in either way; a brand-new one is refused without it.
 *
 * **The link is absolute, on the apex, from every host.** Google sign-in
 * begins and returns on `augrank.com` — the registered `redirect_uri` is fixed
 * — and the OAuth state cookie is host-only, so a start on any other host
 * parks a cookie the callback never sees. The edge already sends `www.` to
 * the apex with a 301; building the href on `brand.apexUrl` means the browser
 * never takes that detour, and a host the edge did not think of cannot start
 * a sign-in it cannot finish. `brand.apexUrl` is empty only before branding
 * has loaded, when the link is a same-origin path as it always was.
 *
 * **The "or" separator belongs to this component, not to the page.** It only
 * means anything when there is something above it to be an alternative *to*.
 * Left in the two pages it outlived the button it divided from: with no OAuth
 * client configured, `app.augrank.com` served a horizontal rule across an empty
 * gap above the email field — which is every deployment until the id reaches
 * Parameter Store, and was the live sign-in screen on 2026-07-26.
 */
export function GoogleButton({
  label,
  acceptedTerms = false,
  workspace,
}: {
  label: string
  /** Set on the signup page, where the terms checkbox is. Never on login. */
  acceptedTerms?: boolean
  /**
   * On a tenant host: the workspace to come back to. The link leaves this
   * host for the apex either way, and `?workspace=` is how the callback knows
   * which host to hand the session to (spec: *Sessions and the handoff*).
   */
  workspace?: string
}): JSX.Element | null {
  const brand = useBrand()
  const available = useGoogleSignIn()
  if (!available) return null

  const start = `${brand.apexUrl}/api/v1/auth/google/start`
  const href = workspace
    ? `${start}?workspace=${encodeURIComponent(workspace)}`
    : `${start}?accepted_terms=${acceptedTerms ? 'true' : 'false'}`
  return (
    <>
      <a className="app-oauth" href={href}>
        <GoogleMark />
        {label}
      </a>
      <div className="app-or">or</div>
    </>
  )
}

/**
 * Whether this deployment can offer Google at all.
 *
 * `null` while unknown, and the button renders nothing until the answer
 * arrives — a button that appears and then vanishes is worse than one that
 * appears a moment late, because the vanishing one may go while a finger is
 * already moving toward it.
 *
 * Not `useQuery`: this has no loading state to render and no error state worth
 * showing. A failure means "we could not ask", and the honest response to that
 * is the same as `false` — offer the password form, which always works.
 */
function useGoogleSignIn(): boolean {
  const [available, setAvailable] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    void (async () => {
      try {
        const result = await call(() =>
          api.GET('/api/v1/auth/providers', { signal: controller.signal }),
        )
        if (!controller.signal.aborted && result.ok) setAvailable(result.data.google)
      } catch {
        // `call()` re-throws only AbortError, which is this effect cleaning up.
      }
    })()
    return () => {
      controller.abort()
    }
  }, [])

  return available
}

/** Google's own mark, inline. A remote asset would be a third-party request on the sign-in page. */
function GoogleMark(): JSX.Element {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.7 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.9 24.6c0-1.6-.1-3.2-.4-4.6H24v9.1h12.9c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 6.9-10 6.9-17.5z"
      />
      <path
        fill="#FBBC05"
        d="M10.4 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.8-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.6 10.8l7.8-6.1z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.5 0 11.9-2.1 15.9-5.9l-7.5-5.8c-2.1 1.4-4.8 2.2-8.4 2.2-6.3 0-11.7-3.7-13.6-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z"
      />
    </svg>
  )
}
